<template>
	<div>
		<div v-if="!countries_loaded">
			<LoadingSpinner class="col-12" />
		</div>

		<div v-if="(type_monte == 'iaf' || type_monte == 'mm' || type_monte == 'mn') && cmep_model" class="mb-2">
			{{ cmep_model.tiers_rs }}
			<span v-if="cmep_model.tiers_address1">
				- {{ cmep_model.tiers_address1 | multiCapitalize }}
				<span v-if="cmep_model.tiers_address2">{{ cmep_model.tiers_address2 | multiCapitalize }} </span>
				<span v-if="cmep_model.tiers_postalcode && cmep_model.tiers_postalcode > 0">, {{ cmep_model.tiers_postalcode }} </span>
				<span v-if="cmep_model.tiers_town">{{ cmep_model.tiers_town | multiCapitalize }} </span>
				<span v-if="cmep_model.tiers_pays">, {{ cmep_model.tiers_pays | uppercase }}</span>
			</span>
		</div>

		<template v-if="countries_loaded && !(type_monte == 'iaf' || type_monte == 'mm' || type_monte == 'mn')">
			<template v-if="!fixed_tiers">
				<div v-if="!disabled" class="form-group mb-3">
					<label class="mb-1">{{ $t('monte.cmep_label_pays') }}</label>
					<CountryInput v-model="cmep_country" :restricted_table="countries"></CountryInput>
				</div>

				<div v-if="cmep_country == 75 && !disabled" class="form-group mb-3">
					<label class="mb-1">{{ $t('monte.cmep_label_departement') }}</label>
					<e-select
						v-model="cmep_postalcode"
						:placeholder="labelTitlePostalCodeCmep"
						:options="postal_codes"
					>
					</e-select>
				</div>

				<div v-if="cmep_country != 75 && cmep_regions.length > 0 && !disabled" class="form-group mb-3">
					<label class="mb-1">{{ $t('monte.cmep_label_region') }}</label>
					<e-select
						v-model="cmep_region"
						:placeholder="$t('monte.rechercher_region_cmep')"
						:options="cmep_regions"
					>
					</e-select>
				</div>
			</template>

			<div class="form-group mb-0">
				<label class="mb-1">{{ $t('monte.cmep_label_centres') }} <span v-if="required">*</span></label>
				<e-select
					v-model="cmep_model"
					track-by="tiers_id"
					label="tiers_rs"
					:placeholder="labelTitleCmep"
					:selectedLabel="selectedLabelCmep"
					:options="current_tiers_tab"
					:allow-empty="true"
					:show-labels="false"
					:disabled="disabled"
					:sortable="false"
					:loading="loadingTiers"
					@search-change="asyncFind"
					:internal-search="false"
					:class="{ 'is-invalid': errors && errors.indexOf('cmep') > -1 }"
				>
					<template slot="singleLabel" slot-scope="{ option }">
						{{ option.tiers_rs }}
						<span v-if="option.tiers_address1">
							- {{ option.tiers_address1 | multiCapitalize }}
							<span v-if="option.tiers_address2">{{ option.tiers_address2 | multiCapitalize }} </span>
							<span v-if="option.tiers_postalcode && option.tiers_postalcode > 0">, {{ option.tiers_postalcode }} </span>
							<span v-if="option.tiers_town">{{ option.tiers_town | multiCapitalize }} </span>
							<span v-if="option.tiers_pays">, {{ option.tiers_pays | uppercase }}</span>
						</span>
					</template>
					<template slot="option" slot-scope="{ option }">
						{{ option.tiers_rs }}
						<span v-if="option.tiers_address1">
							- {{ option.tiers_address1 | multiCapitalize }}
							<span v-if="option.tiers_address2">{{ option.tiers_address2 | multiCapitalize }} </span>
							<span v-if="option.tiers_postalcode && option.tiers_postalcode > 0">, {{ option.tiers_postalcode }} </span>
							<span v-if="option.tiers_town">{{ option.tiers_town | multiCapitalize }} </span>
							<span v-if="option.tiers_pays">, {{ option.tiers_pays | uppercase }}</span>
						</span>
					</template>
				</e-select>
			</div>
		</template>
	</div>
</template>

<script type="text/javascript">
    import _sortBy from 'lodash/sortBy'
    import _uniq from 'lodash/uniq'
    import TiersMixin from "@/mixins/Tiers.js"
    import ContractMixin from "@/mixins/Contract.js"
	
    export default {
		name: 'ChangementCMEP',
		mixins: [TiersMixin, ContractMixin],
		props: {
			contract_id: { type: Number, default: 0 },
			tiers_tab: { type: Array, default: () => [] },
			disabled: { type: Boolean, default: false },
			cmep: { type: Object, default: null },
            required: { type: Boolean, default: true },
            type_monte: { type: String, default: '' },
			/** Code pays ISO par défaut pour la recherche de CMEP (75 = France). */
			default_country: { type: Number, default: 75 },
			/** Code postal du tiers pour pré-filtrer les CMEP par département. */
			default_postal: { type: String, default: null },
			/** Liste fixe de centres éligibles (restriction par condition). Quand fourni, masque pays/dept et désactive le rechargement API. */
			fixed_tiers: { type: Array, default: null },
        },
		data () {
			return {
				cmep_model: null,
                cmep_country: this.default_country || 75,
				current_tiers_tab: [],
				saved_elements: null,
				loadingTiers: false,
				errors: [],
                cmep_postalcode: this.default_postal ? String(this.default_postal).substring(0, 2) : null,
                cmep_region: null,
                cmep_regions: [],
                postal_codes: [],
                countries: [],
                countries_loaded: false,
                labelTitlePostalCodeCmep: this.getTrad("monte.rechercher_postalcode_cmep"),
                most_used_cmep: null,
				labelTitleCmep: this.getTrad("monte.rechercher_cmep"),
				selectedLabelCmep: this.getTrad("global.selected_label"),
			}
		},
		mounted() {
			this.init_component()
		},
		methods: {
			async init_component() {
                this.cmep_model = this.cmep

                // Mode liste fixe (centres restreints par la condition de monte)
                if(this.fixed_tiers) {
                    this.current_tiers_tab = _sortBy(this.fixed_tiers, ['tiers_rs'])
                    this.countries_loaded = true
                    return
                }

                this.postal_codes = await this.loadPostalCodesCmep()
                this.postal_codes = Object.values(this.postal_codes[0])
                this.postal_codes = _uniq(this.postal_codes)
                this.postal_codes = this.postal_codes.sort((a,b) => (a > b) ? 1 : ((b > a) ? -1 : 0))

                const countriesRaw = await this.loadCountriesCmep()
                if (countriesRaw && countriesRaw[0]) {
                    this.countries = _uniq(Object.values(countriesRaw[0]))
                }

                // Si le pays courant n'a pas de centre CMEP, basculer sur la France (75) par défaut
                if (this.countries.length > 0 && !this.countries.includes(this.cmep_country)) {
                    this.cmep_country = this.countries.includes(75) ? 75 : this.countries[0]
                }

                this.countries_loaded = true

                // Charger les régions si pays != France
                if (this.cmep_country != 75) {
                    this.cmep_regions = await this.loadRegionsCmep(this.cmep_country)
                }

                // Si un département de départ est fourni (France), charger les centres correspondants
                if(this.cmep_postalcode && this.cmep_country == 75) {
                    let pc = parseInt(this.cmep_postalcode.substring(0, 2))
                    if(pc < 10) pc = pc.toString().padStart(2, '0')
                    this.current_tiers_tab = await this.loadCmepTiersWithCountry(this.cmep_country, pc)
                    this.current_tiers_tab = _sortBy(this.current_tiers_tab, ['tiers_rs'])
                } else {
                    this.current_tiers_tab = await this.loadCmepTiersWithCountry(this.cmep_country, 0)
                    this.current_tiers_tab = _sortBy(this.current_tiers_tab, ['tiers_rs'])
                }

                const most_used_cmep = await this.getMostUsedCmep()
                if(most_used_cmep) {
				    this.most_used_cmep = this.current_tiers_tab.find(t => t.tiers_id == most_used_cmep)
                }
			},
            asyncFind (query) {
                if(this.saved_elements === null){
                    this.saved_elements = this.deppCloneObj(this.current_tiers_tab)
                }

                if(query == ""){
                    this.current_tiers_tab = this.saved_elements
                    return true
                }

                this.current_tiers_tab = []

                let current_elements = this.deppCloneObj(this.saved_elements)
                let tab_elems = []

                tab_elems = current_elements.filter((elem, key) => {
                    if(elem.tiers_address2 == null) elem.tiers_address2 = ""
                    if(elem.tiers_address1 == null) elem.tiers_address1 = ""
                    if(elem.tiers_postalcode == null) elem.tiers_postalcode = ""
                    if(elem.tiers_town == null) elem.tiers_town = ""
                    if(elem.tiers_pays == null) elem.tiers_pays = ""

                    return elem.tiers_rs.toLowerCase().indexOf(query.toLowerCase()) > -1 || 
                        elem.tiers_address1.toLowerCase().indexOf(query.toLowerCase()) > -1 || 
                        elem.tiers_address2.toLowerCase().indexOf(query.toLowerCase()) > -1 || 
                        elem.tiers_postalcode.toLowerCase().indexOf(query.toLowerCase()) > -1 || 
                        elem.tiers_town.toLowerCase().indexOf(query.toLowerCase()) > -1 ||
                        elem.tiers_pays.toLowerCase().indexOf(query.toLowerCase()) > -1
                })

                this.current_tiers_tab = _sortBy(tab_elems, ['tiers_rs'])
            },
            loadCmep() {
                this.cmep_model = this.most_used_cmep
            }
		},
		watch: {
			cmep_model(val){
				this.$emit("update:cmep", val)
			},
            async default_country(val) {
                if(this.fixed_tiers) return
                this.cmep_country = val || 75
            },
            async cmep_country(val) {
                if(this.fixed_tiers) return
                this.cmep_model = null
                this.cmep_postalcode = null
                this.cmep_region = null
                this.cmep_regions = []

                if (val != 75) {
                    this.cmep_regions = await this.loadRegionsCmep(val)
                }

                this.current_tiers_tab = await this.loadCmepTiersWithCountry(val, 0)
                this.current_tiers_tab = _sortBy(this.current_tiers_tab, ['tiers_rs'])
                this.saved_elements = null
            },
            async cmep_postalcode(val) {
                if(this.fixed_tiers) return
                this.cmep_model = null
                if(this.cmep_country == 75) {
                    let pc = val
                    if(val && typeof val === 'string') {
                        pc = parseInt(val.substring(0,2))
                        if(pc < 10) {
                            pc = pc.toString().padStart(2, '0')
                        }
                    }
                    this.current_tiers_tab = await this.loadCmepTiersWithCountry(this.cmep_country, pc)
                    this.current_tiers_tab = _sortBy(this.current_tiers_tab, ['tiers_rs'])
                    this.saved_elements = null
                }
            },
            async cmep_region(val) {
                if(this.fixed_tiers) return
                if(this.cmep_country == 75) return
                this.cmep_model = null
                this.current_tiers_tab = await this.loadCmepTiersWithCountry(this.cmep_country, val || 0)
                this.current_tiers_tab = _sortBy(this.current_tiers_tab, ['tiers_rs'])
                this.saved_elements = null
            },
            cmep(val) {
                if(val) {
                    this.cmep_model = val
                }
            },
            fixed_tiers(val) {
                this.cmep_model = null
                this.current_tiers_tab = val ? _sortBy(val, ['tiers_rs']) : _sortBy(this.current_tiers_tab, ['tiers_rs'])
            }
		},
        components: {
            CountryInput : () => import('GroomyRoot/components/Inputs/CountryInput'),
            LoadingSpinner : () => import('GroomyRoot/components/Logos/LoadingSpinner_35')
        }
	}

</script>
